import { useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0Dialog, F0Select } from '@factorialco/f0-react'
import { LegalEntitiesWithTinDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

interface LegalEntityOption {
  id: string
  legalName: string
  tin: string | null
  main: boolean
}

interface LegalEntityModalProps {
  onClose: () => void
  onConfirm: (legalEntityId: string) => void
  mutationLoading?: boolean
}

export function LegalEntityModal({ onClose, onConfirm, mutationLoading }: LegalEntityModalProps) {
  const { data, loading: queryLoading, error } = useQuery(LegalEntitiesWithTinDocument)
  const [selectedId, setSelectedId] = useState<string | undefined>()

  const legalEntities: LegalEntityOption[] =
    data?.companies?.legalEntitiesConnection?.edges?.map((edge) => ({
      id: edge.node.id,
      legalName: edge.node.legalName,
      tin: edge.node.tin ?? null,
      main: edge.node.main,
    })) ?? []

  useEffect(() => {
    if (!data || selectedId) return
    const edges = data.companies?.legalEntitiesConnection?.edges ?? []
    const mainEntity = edges.find((e) => e.node.main)
    setSelectedId(mainEntity?.node.id ?? edges[0]?.node.id)
  }, [data, selectedId])

  const selectedEntity = legalEntities.find((le) => le.id === selectedId)
  const isMissingTin = selectedEntity && !selectedEntity.tin

  const options = legalEntities.map((le) => ({
    label: le.legalName,
    value: le.id,
  }))

  return (
    <F0Dialog
      isOpen
      onClose={onClose}
      title={i18n.t('trainings.fundae.finish_group_table.export.legal_entity_modal.title')}
      primaryAction={{
        label: i18n.t('trainings.fundae.finish_group_table.export.legal_entity_modal.confirm'),
        onClick: () => {
          if (selectedId) onConfirm(selectedId)
        },
        loading: queryLoading || mutationLoading,
        disabled: !selectedId || !!isMissingTin || queryLoading || !!error || mutationLoading,
      }}
      secondaryAction={{
        label: i18n.t('trainings.fundae.finish_group_table.export.legal_entity_modal.cancel'),
        onClick: onClose,
      }}
    >
      <Box gap="s16" flexDirection="column">
        <Text color="grey700" size="300">
          {i18n.t('trainings.fundae.finish_group_table.export.legal_entity_modal.description')}
        </Text>
        <F0Select
          label={i18n.t(
            'trainings.fundae.finish_group_table.export.legal_entity_modal.select_label'
          )}
          options={options}
          value={selectedId}
          onChange={(value: string) => setSelectedId(value)}
          loading={queryLoading}
        />
        {isMissingTin && (
          <F0Alert
            variant="critical"
            title={i18n.t(
              'trainings.fundae.finish_group_table.export.legal_entity_modal.no_tin_warning'
            )}
            description={i18n.t(
              'trainings.fundae.finish_group_table.export.legal_entity_modal.no_tin_warning_description'
            )}
          />
        )}
      </Box>
    </F0Dialog>
  )
}
